import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Dv360MediaUpload from '../../../AdDv360Create/AdDv360CreateForm/Steps/FileUploadStep/Dv360MediaUpload'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { updateAd } from '../../../../../../modules/actions/ads'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { lineItemSelector } from '../../../../../../modules/selectors/lineItems'

import { DV_360_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { ASSET_ID } from '../../../../../Google/AdForms/fields'
import { MEDIA_HEIGHT, MEDIA_WIDTH } from '../../../fields'
import { editDv360ImageSizeValidation, initialValues, validationSchema } from './fields'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import {
  LINE_ITEM_TYPE,
  VIDEO_DEFAULT
} from '../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { dv360VideoAdValidationSchema } from '../../../AdDv360Create/AdDv360CreateForm/Routes/CreativeFileUploadRoute/validation'
import { MEDIA_ITEMS } from '../../../AdDv360Create/AdDv360CreateForm/Routes/CreativeFileUploadRoute/fields'

const AssetMediaSectionForm = ({ editItemData: ad, successSubmit, isInternalAd, title, ...formProps }) => {
  const dispatch = useDispatch()
  // set latest media values as initial values to avoid warning message "unsaved changes" on form close after successful submit
  const [handledInitialValues, setHandledInitialValues] = useState(initialValues)

  const { [LINE_ITEM_TYPE]: lineItemType } = useSelector(lineItemSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const { id, account: adAccountId } = ad
  const adAccount = adAccountId || selectedAdAccountId

  const width = ad?.dimensions[MEDIA_WIDTH]
  const height = ad?.dimensions[MEDIA_HEIGHT]
  const itemPath = `${MEDIA_ITEMS}[0]`

  const handleSubmit = useCallback(
    values => {
      const newAsset = values[MEDIA_ITEMS][0]
      dispatch(
        updateAd(
          {
            account: adAccount,
            assets: [
              {
                asset: {
                  media_id: newAsset[ASSET_ID]
                },
                role: 'ASSET_ROLE_MAIN'
              }
            ],
            ...(isInternalAd && { internal: true }),
            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          id,
          DV_360_PLATFORM
        )
      )
    },
    [dispatch, id, isInternalAd, adAccount, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ handleSubmit })

  const formik = useFormik({
    initialValues: handledInitialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true
  })

  const purifiedFormik = usePurifiedFormik(formik)

  // image file which is uploaded should have the same as current image dimensions
  const fileValidationSchema = useMemo(() => {
    if (lineItemType === VIDEO_DEFAULT) {
      // video file has no dimensions, so we use regular video validation schema
      return dv360VideoAdValidationSchema
    } else {
      return editDv360ImageSizeValidation({ width, height })
    }
  }, [width, height, lineItemType])

  useEffect(() => {
    // workaround to set formik values and initial values to avoid issue with warning message
    // this will lead to run checkUnsavedChanges function in EditForm
    // and skip warning message "unsaved changes" on successful submit form close
    if (successSubmit || manualSuccessSubmit) {
      const newInitialValues = {
        ...formik.values,
        wasSubmitted: 'true'
      }
      setHandledInitialValues(newInitialValues)
      formik.setValues(newInitialValues)
    }
  }, [successSubmit, manualSuccessSubmit, formik])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={handledInitialValues}
      successSubmit={successSubmit || manualSuccessSubmit}
      {...formProps}
    >
      <p>
        Select image with width {width}px and height {height}px{' '}
      </p>
      <Dv360MediaUpload
        formik={formik}
        itemPath={itemPath}
        onMediaRemove={() => purifiedFormik.setFieldValue(MEDIA_ITEMS, initialValues[MEDIA_ITEMS])}
        index={0}
        key={ad.id}
        fileValidationSchema={fileValidationSchema}
        allowMultipleFiles={false}
      />
    </EditForm>
  )
}

export default AssetMediaSectionForm
